/**
 * Messaging Service
 * API wrapper for in-app messaging (conversations, email verification for guests)
 */

import API_BASE_URL from '../config';

const GUEST_TOKEN_KEY = 'ws_guest_msg_token';
const GUEST_EMAIL_KEY = 'ws_guest_msg_email';

// ============================================================================
// GUEST TOKEN MANAGEMENT (localStorage — persists across sessions, 24hr token)
// ============================================================================

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return !payload.exp || payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function getStoredGuestMsgToken(): string | null {
  try {
    const token = localStorage.getItem(GUEST_TOKEN_KEY);
    if (token && isTokenExpired(token)) {
      clearGuestMsgToken();
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function getStoredGuestMsgEmail(): string | null {
  try {
    // Only return email if we still have a valid token
    if (!getStoredGuestMsgToken()) return null;
    return localStorage.getItem(GUEST_EMAIL_KEY);
  } catch {
    return null;
  }
}

export function storeGuestMsgToken(token: string, email: string): void {
  try {
    localStorage.setItem(GUEST_TOKEN_KEY, token);
    localStorage.setItem(GUEST_EMAIL_KEY, email);
  } catch {
    // localStorage unavailable
  }
}

export function clearGuestMsgToken(): void {
  try {
    localStorage.removeItem(GUEST_TOKEN_KEY);
    localStorage.removeItem(GUEST_EMAIL_KEY);
  } catch {
    // ignore
  }
}

// ============================================================================
// AUTH HELPERS
// ============================================================================

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  if (token) {
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }
  const guestToken = getStoredGuestMsgToken();
  if (guestToken) {
    return { 'X-Guest-Token': guestToken, 'Content-Type': 'application/json' };
  }
  return { 'Content-Type': 'application/json' };
}

export function isSignedIn(): boolean {
  return !!localStorage.getItem('auth_token');
}

// ============================================================================
// EMAIL VERIFICATION (guests only)
// ============================================================================

export async function sendVerificationCode(email: string): Promise<{ success: boolean; error?: string }> {
  const res = await fetch(`${API_BASE_URL}/api/verify-email/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) return { success: false, error: data.error || 'Failed to send code' };
  return { success: true };
}

export async function confirmVerificationCode(
  email: string,
  code: string,
): Promise<{ success: boolean; guest_token?: string; error?: string }> {
  const res = await fetch(`${API_BASE_URL}/api/verify-email/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  const data = await res.json();
  if (!res.ok) return { success: false, error: data.error || 'Invalid code' };
  // Store token for reuse within session
  if (data.guest_token) {
    storeGuestMsgToken(data.guest_token, email);
  }
  return { success: true, guest_token: data.guest_token };
}

// ============================================================================
// CONVERSATIONS
// ============================================================================

export interface StartConversationParams {
  recipient_id: number;
  item_id?: number;
  collection_id?: number;
  message: string;
  guest_name?: string;
}

export async function startConversation(
  params: StartConversationParams,
): Promise<{ success: boolean; conversation_id?: number; message_id?: number; error?: string }> {
  const res = await fetch(`${API_BASE_URL}/api/conversations`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok) return { success: false, error: data.error || 'Failed to send message' };
  return { success: true, conversation_id: data.conversation_id, message_id: data.message_id };
}

export interface Conversation {
  id: number;
  item: { id: number; name: string; image_url?: string } | null;
  other_user: { id: number | null; display_name: string };
  last_message: { body: string; created_at: string; is_mine: boolean } | null;
  unread_count: number;
  updated_at: string;
}

export async function listConversations(): Promise<Conversation[]> {
  const res = await fetch(`${API_BASE_URL}/api/conversations`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.conversations || [];
}

export interface Message {
  id: number;
  sender_name: string;
  is_mine: boolean;
  body: string;
  created_at: string;
}

export interface ConversationDetail {
  conversation: {
    id: number;
    item: { id: number; name: string; image_url?: string } | null;
    other_user: { id: number | null; display_name: string };
    status: string;
    created_at: string;
  };
  messages: Message[];
}

export async function getConversation(id: number): Promise<ConversationDetail | null> {
  const res = await fetch(`${API_BASE_URL}/api/conversations/${id}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return null;
  return res.json();
}

export async function sendReply(
  conversationId: number,
  body: string,
): Promise<{ success: boolean; message_id?: number; error?: string }> {
  const res = await fetch(`${API_BASE_URL}/api/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ body }),
  });
  const data = await res.json();
  if (!res.ok) return { success: false, error: data.error || 'Failed to send reply' };
  return { success: true, message_id: data.message_id };
}

export async function markRead(conversationId: number): Promise<void> {
  await fetch(`${API_BASE_URL}/api/conversations/${conversationId}/read`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
  });
}

export async function getUnreadCount(): Promise<number> {
  const res = await fetch(`${API_BASE_URL}/api/conversations/unread-count`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return 0;
  const data = await res.json();
  return data.unread_count || 0;
}

export interface UserSearchResult {
  id: number;
  display_name: string;
  email: string;
}

export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  if (!query.trim()) return [];
  const res = await fetch(`${API_BASE_URL}/api/users/search?q=${encodeURIComponent(query.trim())}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.users || [];
}

export async function archiveConversation(id: number): Promise<boolean> {
  const res = await fetch(`${API_BASE_URL}/api/conversations/${id}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ status: 'archived' }),
  });
  return res.ok;
}
